import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, CalendarDays } from "lucide-react";
import { MaskLines, EASE } from "./motion";
import { goToContact, openCal } from "./smooth";

const fade = (d) => ({ initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 }, transition: { duration: 1, ease: EASE, delay: d } });

const Portrait = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const move = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const leave = () => { mx.set(0); my.set(0); };

  return (
    <motion.div {...fade(0.3)} className="relative mx-auto w-full max-w-[230px] [perspective:1200px] sm:max-w-[300px] lg:ml-0 lg:max-w-[360px]" onMouseMove={move} onMouseLeave={leave}>
      <div className="absolute -inset-10 rounded-full bg-orange/25 blur-[90px]" />
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]" data-testid="hero-portrait">
        <div className="clip-frame relative aspect-[4/5] overflow-hidden bg-navy-card">
          <motion.img
            src="/img/rui-julho.webp"
            alt="Rui Guedes, fundador da Soluções Ideais"
            className="h-full w-full object-cover object-top"
            initial={{ scale: 1.25 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.8, ease: EASE, delay: 0.4 }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/10 to-transparent" />
        </div>
        <div className="absolute -left-6 bottom-6 hidden [transform:translateZ(60px)] sm:-left-8 sm:block">
          <div className="rounded-2xl border border-white/15 bg-navy/80 px-4 py-3 backdrop-blur-xl">
            <p className="font-display text-lg font-extrabold text-white">Rui Guedes</p>
            <p className="text-xs text-slate-300">Fundador · Soluções Ideais</p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  return (
    <section id="top" data-testid="hero-container" className="grain relative overflow-hidden bg-navy pb-20 pt-32 text-white sm:pt-40 lg:pb-28">
      <div className="spot pointer-events-none absolute left-[-10%] top-[-20%] h-[700px] w-[700px]" />

      <div className="relative mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-14">
        <div className="lg:col-span-4 lg:pt-10"><Portrait /></div>
        <div className="lg:col-span-8">
          <motion.p {...fade(0.1)} className="mb-6 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-orange">
            <span className="relative flex h-2 w-2"><span className="absolute h-full w-full animate-ping rounded-full bg-orange opacity-70" /><span className="h-2 w-2 rounded-full bg-orange" /></span>
            Fundador e CEO da Soluções Ideais
          </motion.p>
          <h1 data-testid="hero-name" className="font-display text-[4.2rem] font-black uppercase leading-[0.86] tracking-[-0.04em] sm:text-[7rem] lg:text-[7.5rem] xl:text-[9rem]">
            <MaskLines onLoad baseDelay={1} lines={["Rui", <span className="text-orange">Guedes.</span>]} />
          </h1>
          <motion.p {...fade(0.8)} data-testid="hero-signature-quote" className="mt-8 max-w-2xl font-display text-2xl font-extrabold leading-tight sm:text-3xl">
            “Troquei a segurança pela possibilidade. <span className="text-orange">De pessoa comum a empresário.”</span>
          </motion.p>
          <motion.div {...fade(1.1)} className="mt-8 grid gap-8 md:grid-cols-2 md:gap-10">
            <p className="font-display text-lg font-bold leading-snug sm:text-xl">
              <span className="block text-base font-medium text-slate-300 sm:text-lg">Com método, acompanhamento e propósito. Sem perder aquilo que realmente importa.</span>
            </p>
            <p className="border-l-2 border-orange pl-5 text-sm leading-relaxed text-slate-300 sm:text-base" data-testid="hero-subheadline">
              Há quase 30 anos tomei uma decisão que mudou a minha vida. Troquei uma carreira segura pela possibilidade de construir algo maior. Hoje lidero uma rede nacional e ajudo outras pessoas a fazer o mesmo através de um modelo comprovado de franchising.
            </p>
          </motion.div>
          <motion.div {...fade(1.2)} data-testid="hero-experience-stat" className="mt-8 flex items-center gap-4">
            <span className="font-display text-6xl font-black tracking-tight text-orange sm:text-7xl">+30</span>
            <span className="max-w-[220px] font-display text-base font-extrabold uppercase leading-tight tracking-tight text-white sm:text-lg">anos de experiência no setor imobiliário</span>
          </motion.div>
          <motion.div {...fade(1.3)} className="mt-10 flex flex-col gap-3 sm:flex-row sm:gap-4">
            <button onClick={() => goToContact("conhecer")} data-testid="hero-cta-primary" className="group flex items-center justify-center gap-3 rounded-full bg-orange px-7 py-4 font-bold text-white shadow-[0_20px_60px_-15px_rgba(255,94,30,0.7)] transition-[background-color,transform] duration-300 hover:-translate-y-1 hover:bg-orange-deep">
              Quero Conhecer a Oportunidade
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-45" />
            </button>
            <button onClick={() => openCal()} data-testid="hero-cta-secondary" className="flex items-center justify-center gap-3 rounded-full border border-white/25 px-7 py-4 font-bold text-white transition-[background-color,border-color] duration-300 hover:border-white hover:bg-white/10">
              <CalendarDays className="h-5 w-5" /> Agendar Conversa
            </button>
          </motion.div>
        </div>
      </div>

      <motion.div {...fade(1.6)} className="relative mx-auto mt-16 hidden max-w-[1400px] items-center gap-3 px-8 font-mono text-xs uppercase tracking-[0.2em] text-slate-300 lg:flex">
        <ArrowDown className="h-4 w-4 animate-bounce text-orange" /> A história começa em 1997
      </motion.div>
    </section>
  );
};
